import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useStateValue } from './StateProvider';

import axios from 'axios';
import configData from './Config';

import UpdatePage from "./UpdatePage";
import PrivacyPolicy from "./PrivacyPolicy"
import AuthScreen from "./AuthScreen";
import Projects from "./Projects";
import HomePage from "./HomePage";
import Home from "./Home";
import RemoveAccount from "./RemoveAccount"
import Modal from "./Modal.js";

import Spinner from './Spinner.js';

import { areObjectsEqual } from './Utils/textparse';

import './App.css';

const REFRESHUPDATEINTERVAL = 180000;  // every 3 minutes
const REFRESHVERSIONINTERVAL = 43200000;  // every 12 hours

function App() {
    const initialState = {
        user: {
            userid: '',
            usertoken: '',
            usertype: 0,
            useremail: '',
            username: '',
            usernotifications: [],
            subscriptionid: '',
            subscriptionplan: '',
            subscriptionstart: '',
            subscriptionend: '',
            subscriptionmaxusers: 0,
            subscriptionmaxprojects: 0,
        },
        version: 0,
        modalopen: false,
        modalcontent: null,
        modaltitle: null,
        modaltype: null,
    }

    const [state, setState] = useState(initialState);

    const [{ project }, dispatch] = useStateValue();

    const [loading, setLoading] = useState(false);
    const [versionlock, setVersionlock] = useState(false);

    const fetchVersionData = async () => {
        const response = await fetch('/manifest.json');
        const manifestData = await response.json();

        setState(state => ({
            ...state,
            version: manifestData.version
        }))

        axios.post(configData.CONTROLLERURL + configData.GETVERSION, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json;charset=UTF-8"
            },
        }).then((res) => {
            if (res.data == manifestData.version) {
                setVersionlock(false);
            }
            else {
                setVersionlock(true);
            }
        }).catch(() => {
            console.log('Not connected, cant check for version')
        })
    }

    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchVersionData();
        }, REFRESHVERSIONINTERVAL);

        return () => clearInterval(intervalId);
    }, []);

    const getLastModified = (project, state, dispatch) => {
        if (project.projectid) {
            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid
            };

            axios.post(configData.CONTROLLERURL + configData.GETLASTMODIFIED, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                if (res.data != project.lastmodified) {
                    const data = {
                        projectuserid: project.projectuserid,
                        projectid: project.projectid
                    };

                    axios.post(configData.CONTROLLERURL + configData.GETPROJECT, data, {
                        headers: {
                            "Accept": "application/json",
                            "Content-Type": "application/json;charset=UTF-8",
                            "userid": state.user.userid,
                            "usertoken": state.user.usertoken
                        }
                    }).then((res) => {
                        if (res.data) {
                            const changedKeys = areObjectsEqual(project, res.data);
                            if (changedKeys.length > 0) {
                                changedKeys.forEach(key => {
                                    dispatch({
                                        type: 'UPDATE_KEYVALUE',
                                        key: key,
                                        value: res.data[key]
                                    });
                                });
                            }
                        }
                    })
                }
            }).catch((err) => {
                console.log(err.response?.data?.message || err.message || 'Network error');
            })
        }
    };

    useEffect(() => {
        if (project.archived) return;

        const intervalId = setInterval(() => {
            getLastModified(project, state, dispatch);
        }, REFRESHUPDATEINTERVAL);

        return () => clearInterval(intervalId);
    }, [project, state, dispatch]);

    useEffect(() => { //20250707
        getCookieUser().then(value => {
            validateUser(value.userid, value.usertoken)
        })
    }, []);

    async function getCookieUser() { //20250707
        let result = { userid: '', usertoken: '' };
        const cookie = document.cookie.split("; ").find(c => c.startsWith("user="));

        if (!cookie) return result;

        const value = cookie.substring(5);

        const useridMatch = value.match(/userid=([^&]+)/);
        const tokenMatch = value.match(/usertoken=(.+)/);

        result = { userid: useridMatch[1], usertoken: tokenMatch[1] };

        return result;
    }

    function validateUser(userid, usertoken) { //20240523
        setLoading(true)

        const data = {
            userid: userid,
            usertoken: usertoken
        }

        axios.post(configData.AUTHENTICATIONCONTROLLER + configData.VALIDATEUSER, data, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json;charset=UTF-8"
            },
        }).then(res => {
            if (res.data instanceof Object) {
                if (res.data.code === 1) {
                    setState(state => ({
                        ...state,
                        user: {
                            userid: userid,
                            usertoken: usertoken,
                            useremail: res.data.data.useremail,
                            username: res.data.data.username,
                            usertype: res.data.data.usertype,
                            subscriptionid: res.data.data.subscriptionid,
                            subscriptionplan: res.data.data.subscriptionplan,
                            subscriptionstart: res.data.data.subscriptionstart,
                            subscriptionend: res.data.data.subscriptionend,
                            subscriptionmaxusers: res.data.data.subscriptionmaxusers,
                            subscriptionmaxprojects: res.data.data.subscriptionmaxprojects,
                        }
                    }))
                }
                else {
                }
            }
            else {
            }
        }).catch(() => {

        }).finally(() => {
            setLoading(false)
        })
    }

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
            }
        };

        const handlePopState = () => {
            setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
        };

        document.addEventListener('keydown', handleEscape);
        window.addEventListener('popstate', handlePopState);

        return () => {
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [setState]);


    if (loading) {
        return (
            <div className='app_spinnercontainer'>
                <Spinner loading={true} size={200} />
            </div>
        )
    }

    return (
        versionlock ?
            <BrowserRouter>
                <Routes>
                    <Route index element={<UpdatePage />} />
                </Routes>
            </BrowserRouter>
            :
            state.user.userid ?
                <BrowserRouter>
                    <Modal state={state} setState={setState} />
                    <Routes>
                        <Route index element={<Projects state={state} setState={setState} />} />
                        <Route path='home' element={<Home state={state} setState={setState} />} />
                        <Route path="*" element={<HomePage state={state} setState={setState} />} />
                        <Route path='removeaccount' element={<RemoveAccount />} />
                    </Routes>
                </BrowserRouter>
                :
                <BrowserRouter>
                    <Modal state={state} setState={setState} />
                    <Routes>
                        <Route index element={<HomePage state={state} setState={setState} />} />
                        <Route path='authscreen' element={<AuthScreen state={state} setState={setState} />} />
                        <Route path='privacypolicy' element={<PrivacyPolicy />} />
                        <Route path='removeaccount' element={<RemoveAccount />} />
                    </Routes>
                </BrowserRouter>
    );
}

export default App;